import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
import Link from '@atlaskit/link';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { FormattedMessage } from '@atlassian/jira-intl';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import {
	ContextualAnalyticsData,
	BANNER,
	useAnalyticsEvents,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useUiModificationsErrors } from '@atlassian/jira-ui-modifications-view-gic/src/ui/use-ui-modifications-errors/index.tsx';
import { useUiModificationsNotifications } from '@atlassian/jira-ui-modifications-view-gic/src/ui/use-ui-modifications-notifications/index.tsx';
import messages from './messages.tsx';
import { getDisplayErrorString } from './utils.tsx';

const SectionMessages = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ meta }] = useFormContext();

	const [issueAdjustmentError] = useUiModificationsErrors();
	const [uiModificationsNotification] = useUiModificationsNotifications();
	const { isSiteAdmin } = useTenantContext();

	useEffect(() => {
		if (!meta.areRequiredFieldsSupported) {
			const analyticsEvent = createAnalyticsEvent({
				actionSubject: 'banner',
				action: 'viewed',
			});
			fireUIAnalytics(analyticsEvent, 'globalIssueCreateMandatoryFieldsUnsupported');
		}
	}, [createAnalyticsEvent, meta.areRequiredFieldsSupported]);

	if (meta.queryErrorMessage !== '') {
		return (
			<SectionMessage
				appearance="error"
				testId="issue-create.ui.modal.create-form.section-messages.query-error-message"
			>
				{meta.queryErrorMessage}
			</SectionMessage>
		);
	}

	if (meta.submitErrorMessage !== undefined && meta.submitErrorMessage !== '') {
		return (
			<SectionMessage
				appearance="error"
				testId="issue-create.ui.modal.create-form.section-messages.submit-error-message"
			>
				{getDisplayErrorString(meta.submitErrorMessage).map((error, index) => (
					<MessageWrapper key={index}>{error}</MessageWrapper>
				))}
			</SectionMessage>
		);
	}

	let infoMessage;
	if (isSiteAdmin) {
		infoMessage = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
			? messages.requiredFieldsUnsupportedForAdminIssueTermRefresh
			: messages.requiredFieldsUnsupportedForAdmin;
	} else {
		infoMessage = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
			? messages.requiredFieldsUnsupportedForUserIssueTermRefresh
			: messages.requiredFieldsUnsupportedForUser;
	}

	if (!meta.areRequiredFieldsSupported) {
		return (
			<ContextualAnalyticsData
				sourceName="globalIssueCreateMandatoryFieldsUnsupported"
				sourceType={BANNER}
			>
				<SectionMessage
					appearance="error"
					testId="issue-create.ui.modal.create-form.section-messages.required-fields-message"
				>
					<FormattedMessage
						{...infoMessage}
						values={{
							a: (chunks) =>
								fg('replace-anchor-with-link__gryffindor') ? (
									<Link href="/jira/contact-administrator">{chunks}</Link>
								) : (
									// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
									<a href="/jira/contact-administrator">{chunks}</a>
								),
							button: () => (
								<ErrorIcon
									label="incompatible-field-error"
									LEGACY_size="medium"
									spacing="spacious"
									color={token('color.icon.subtle')}
								/>
							),
						}}
					/>
				</SectionMessage>
			</ContextualAnalyticsData>
		);
	}

	if (meta.inlineCreateError && meta.inlineCreateError?.errors.length > 0) {
		return (
			<SectionMessage
				appearance="error"
				testId="issue-create.ui.modal.create-form.section-messages.inline-create-errors"
				title={meta.inlineCreateError.title || ''}
			>
				<ul>
					{meta.inlineCreateError.errors.map((error, index) => (
						<li key={`inline-error-${index}`}>{error}</li>
					))}
				</ul>
			</SectionMessage>
		);
	}

	if (issueAdjustmentError !== null) {
		return (
			<SectionMessage
				appearance="error"
				testId="issue-create.ui.modal.create-form.section-messages.ui-modifications-error-message"
			>
				{issueAdjustmentError.message}
			</SectionMessage>
		);
	}

	if (uiModificationsNotification !== null) {
		return (
			<SectionMessage
				appearance={uiModificationsNotification.type}
				testId="issue-create.ui.modal.create-form.section-messages.ui-modifications-notification-message"
			>
				{uiModificationsNotification.message}
			</SectionMessage>
		);
	}
	return null;
};

export default SectionMessages;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageWrapper = styled.pre({
	font: 'inherit',
	whiteSpace: 'pre-wrap',
});
